const BR_TAG = /<br\s*\/?>/gi;
const BR_TOKEN = '\x00BR\x00';
const BR_TOKEN_RE = /\x00BR\x00/g;

const normalizeBrTags = (text: string) => text.replace(BR_TAG, BR_TOKEN);

const escapeHtml = (value: string) =>
	value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');

const SAFE_URL_RE = /^https?:\/\//i;
const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * Escape plain text and convert markdown links to safe <a> tags.
 * BR_TOKEN placeholders are restored to <br/> after escaping.
 */
const formatInline = (text: string): string => {
	const normalized = normalizeBrTags(text);
	const parts: string[] = [];
	let lastIndex = 0;
	let match: RegExpExecArray | null;

	LINK_RE.lastIndex = 0;
	while ((match = LINK_RE.exec(normalized)) !== null) {
		// Escape plain text before this link
		if (match.index > lastIndex) {
			parts.push(escapeHtml(normalized.slice(lastIndex, match.index)));
		}
		const [, label, rawUrl] = match;
		const url = rawUrl.trim();
		if (SAFE_URL_RE.test(url)) {
			parts.push(`<a href="${escapeHtml(url)}">${escapeHtml(label)}</a>`);
		} else {
			parts.push(escapeHtml(label));
		}
		lastIndex = LINK_RE.lastIndex;
	}

	// Escape remaining plain text after last link
	if (lastIndex < normalized.length) {
		parts.push(escapeHtml(normalized.slice(lastIndex)));
	}

	// Restore BR tokens to actual <br/> tags (after escaping so they stay safe)
	return parts.join('').replace(BR_TOKEN_RE, '<br/>');
};

const splitHeadingLine = (line: string) => {
	const headingText = line.slice(2).trim();
	const normalized = normalizeBrTags(headingText);
	const brIndex = normalized.indexOf(BR_TOKEN);

	if (brIndex < 0) {
		return { title: headingText, intro: '' };
	}

	return {
		title: normalized.slice(0, brIndex).trim(),
		intro: normalized.slice(brIndex + BR_TOKEN.length).trim(),
	};
};

export function faqMarkdownToHtml(markdown: string): string {
	return normalizeBrTags(markdown)
		.trim()
		.split(/\n\n+/)
		.map((block) => {
			const lines = block.split('\n').filter((line) => line.length > 0);
			if (lines.length === 0) return '';

			let index = 0;
			let html = '';
			const paragraphLines: string[] = [];
			const listLines: string[] = [];

			if (lines[0].startsWith('# ')) {
				const { title, intro } = splitHeadingLine(lines[0]);
				if (title) {
					html += `<h1>${formatInline(title)}</h1>`;
				}
				if (intro) {
					paragraphLines.push(intro);
				}
				index = 1;
			}

			for (; index < lines.length; index += 1) {
				const line = lines[index];
				if (line.startsWith('- ')) {
					listLines.push(line.slice(2));
				} else {
					if (listLines.length > 0) {
						html += `<ul>${listLines.map((item) => `<li>${formatInline(item)}</li>`).join('')}</ul>`;
						listLines.length = 0;
					}
					paragraphLines.push(line);
				}
			}

			if (paragraphLines.length > 0) {
				html += `<p>${formatInline(paragraphLines.join('<br/>'))}</p>`;
			}

			if (listLines.length > 0) {
				html += `<ul>${listLines.map((item) => `<li>${formatInline(item)}</li>`).join('')}</ul>`;
			}

			return html;
		})
		.join('');
}
